import ScrollModal from "@/components/common/scroll-modal";
import { Heading, Button, Icon } from "@elaniintc/goes-design-system";
import React, { useState, useEffect } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { GripHandle } from "@/components/ui/grip-handle";
import type { FormPage } from "../types";

interface SortStepsFormDesignModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pages: FormPage[];
  onReorder: (pages: FormPage[]) => void;
}

export default function SortStepsFormDesignModal({
  open,
  onClose,
  onSuccess,
  pages,
  onReorder,
}: SortStepsFormDesignModalProps) {
  const [ordered, setOrdered] = useState<FormPage[]>(pages);

  useEffect(() => {
    setOrdered(pages);
  }, [pages]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    onReorder(ordered);
    onSuccess();
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((p) => p.id === active.id);
    const newIndex = ordered.findIndex((p) => p.id === over.id);
    const newOrdered = arrayMove(ordered, oldIndex, newIndex);
    const updatedOrdered = newOrdered.map((p, index, pagesArr) => {
      return {
        ...p,
        sections: p.sections.map((s) => {
          return {
            ...s,
            fields: s.fields.map((f) => {
              const conditions =
                f.conditions?.filter((c) => {
                  const condVarId = c?.data?.selectedVar?.id;
                  const condFieldPageIndex = pagesArr.findIndex((page) => {
                    return page.sections.some((section) => {
                      return section.fields.some(
                        (field) =>
                          (field.technicalKey || field.name) === condVarId,
                      );
                    });
                  });

                  if (condFieldPageIndex === -1) return false;
                  if (condFieldPageIndex <= index) return true;
                  return false;
                }) || [];
              return {
                ...f,
                conditions,
                isConditional: conditions.length > 0,
              };
            }),
          };
        }),
      };
    });
    setOrdered(updatedOrdered);
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] h-full max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            Reordenar pasos
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-sm text-body" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-[120px] cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="button"
            className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={false}
            onClick={handleSubmit}
          >
            Guardar orden
          </Button>
        </div>
      }
    >
      <div className="px-8 py-6">
        <div className="flex">
          {/* Numbers column */}
          <div className="flex flex-col items-end pr-4 min-w-8 border-r border-neutral-quiet">
            {ordered.map((_, idx) => (
              <span
                key={idx}
                className="h-10 flex items-center text-body font-normal text-base"
              >
                {idx + 1}
              </span>
            ))}
          </div>
          {/* Draggable content column */}
          <div className="flex-1 flex flex-col pl-4">
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={ordered.map((p) => p.id)}>
                {ordered.map((page, idx) => (
                  <SortableStepRow
                    key={page.id}
                    id={page.id}
                    name={page.name || "Nombre del paso"}
                    index={idx}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </ScrollModal>
  );
}

interface SortableStepRowProps {
  id: string;
  name: string;
  index: number;
}
const SortableStepRow: React.FC<SortableStepRowProps> = ({ id, name }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.8 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center h-10">
      <div
        {...listeners}
        {...attributes}
        className="mr-2 cursor-grab flex items-center"
        style={{ touchAction: "none" }}
        tabIndex={0}
        aria-label="Mover paso"
      >
        <GripHandle />
      </div>
      <span className="flex-1 text-body font-normal text-base truncate">
        {name}
      </span>
    </div>
  );
};
